import React, { cloneElement } from 'react';
import { motion } from 'framer-motion';
import { Globe, Users, Shield, Landmark, CheckCircle2, Leaf, BookOpen } from 'lucide-react';
export const About = () => {
  return <main className="pt-24" data-id="element-89">
      {/* Hero Section */}
      <section className="bg-brand-navy py-24 text-white relative overflow-hidden" data-id="element-90">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-91">
          <div className="absolute inset-0 opacity-10" data-id="element-92">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80')] bg-cover bg-center" data-id="element-93" />
          </div>
          <span className="text-brand-gold font-bold tracking-widest uppercase text-sm mb-6 block" data-id="element-94">
            About Consilium
          </span>
          <h1 className="text-5xl md:text-7xl font-display mb-8" data-id="element-95">
            Global Expertise. <br data-id="element-96" />
            <span className="italic" data-id="element-97">Institutional</span> Quality.
          </h1>
          <p className="text-xl text-white/70 max-w-2xl leading-relaxed" data-id="element-98">
            Consilium Advisory is a premier multidisciplinary firm delivering
            strategic advice, disciplined execution, and lasting partnerships to
            a global clientele.
          </p>
        </motion.div>
      </section>

      {/* Philosophy */}
      <section className="py-24 bg-brand-warm-white" data-id="element-99">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-7xl mx-auto px-6" data-id="element-100">
          <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-101">
            <div data-id="element-102">
              <h2 className="text-4xl font-display text-brand-navy mb-8" data-id="element-103">
                Our Philosophy
              </h2>
              <div className="space-y-6 text-brand-charcoal/70 text-lg leading-relaxed" data-id="element-104">
                <p data-id="element-105">
                  We believe that complex financial decisions require more than
                  just technical expertise—they require strategic foresight and
                  a deep understanding of the global economic landscape.
                </p>
                <p data-id="element-106">
                  Our firm was founded on the principles of integrity,
                  accountability, and disciplined execution. We don't just
                  advise; we partner with our clients to drive sustainable
                  growth and long-term value.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6" data-id="element-107">
              {[{
              title: 'Integrity',
              icon: <Shield data-id="element-108" />
            }, {
              title: 'Expertise',
              icon: <Globe data-id="element-109" />
            }, {
              title: 'Partnership',
              icon: <Users data-id="element-110" />
            }, {
              title: 'Execution',
              icon: <CheckCircle2 data-id="element-111" />
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white p-8 rounded-2xl shadow-sm border border-brand-navy/5 text-center" data-id="element-112">
                  <div className="text-brand-gold mb-4 flex justify-center" data-id="element-113">
                    {cloneElement(item.icon, {
                  size: 32
                })}
                  </div>
                  <h4 className="font-heading font-bold text-brand-navy" data-id="element-114">
                    {item.title}
                  </h4>
                </motion.div>)}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Initiatives */}
      <section className="py-24 bg-white" data-id="element-115">
        <div className="max-w-7xl mx-auto px-6" data-id="element-116">
          <div className="text-center mb-16" data-id="element-117">
            <h2 className="text-4xl font-display text-brand-navy mb-4" data-id="element-118">
              Strategic Initiatives
            </h2>
            <p className="text-brand-charcoal/60 max-w-2xl mx-auto" data-id="element-119">
              Beyond advisory, we are committed to driving impact across the
              African continent through specialized platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12" data-id="element-120">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="bg-brand-emerald p-12 rounded-3xl text-white" data-id="element-121">
              <Leaf size={48} className="mb-8 text-brand-gold" data-id="element-122" />
              <h3 className="text-3xl font-display mb-6" data-id="element-123">
                Green Harvest Africa
              </h3>
              <p className="text-white/80 mb-8 leading-relaxed" data-id="element-124">
                Pioneering sustainable agricultural investment and
                transformation across the continent. We combine financial
                structure with operational excellence to drive food security and
                economic growth.
              </p>
              <button className="bg-white text-brand-emerald px-8 py-3 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-brand-gold hover:text-white transition-all" data-id="element-125">
                Learn More
              </button>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="bg-brand-navy p-12 rounded-3xl text-white" data-id="element-126">
              <BookOpen size={48} className="mb-8 text-brand-gold" data-id="element-127" />
              <h3 className="text-3xl font-display mb-6" data-id="element-128">Think Africa</h3>
              <p className="text-white/80 mb-8 leading-relaxed" data-id="element-129">
                An editorial and research platform focused on governance,
                policy, youth leadership, and international diplomacy. Shaping
                the future through intellectual rigor and strategic foresight.
              </p>
              <button className="bg-brand-gold text-white px-8 py-3 rounded-full font-bold text-sm uppercase tracking-widest hover:bg-white hover:text-brand-navy transition-all" data-id="element-130">
                Read Research
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-brand-warm-white" data-id="element-131">
        <div className="max-w-7xl mx-auto px-6" data-id="element-132">
          <div className="flex justify-between items-end mb-16" data-id="element-133">
            <div data-id="element-134">
              <h2 className="text-4xl font-display text-brand-navy" data-id="element-135">
                Executive Leadership
              </h2>
              <p className="text-brand-charcoal/60 mt-4" data-id="element-136">
                Led by partners with deep experience in global finance and
                strategic consulting.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-137">
            {[1, 2, 3].map(i => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="group" data-id="element-138">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-6" data-id="element-139">
                  <img src={`https://images.unsplash.com/photo-${i === 1 ? '1560250097-0b93528c311a' : i === 2 ? '1573496359142-b8d87734a5a2' : '1519085360753-af0119f7cbe7'}?auto=format&fit=crop&q=80`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt="Leader" data-id="element-140" />
                </div>
                <h4 className="text-xl font-heading font-bold text-brand-navy" data-id="element-141">
                  Managing Partner
                </h4>
                <p className="text-brand-gold text-sm uppercase tracking-widest mt-1" data-id="element-142">
                  Strategic Advisory
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </main>;
};